import type { GetServerSidePropsContext } from 'next';

import { getLocaleFromParams, removeLocaleFromUrl } from '@/utils/locale';
import { addTrailingSlash, extractPathFromUrl } from '@/utils/urlUtils';

import {
  APOLLO_CLIENTS,
  CustomContext,
  initializeApollo,
  initializeSecureApolloClient,
} from '@/graphql/client';
import type {
  OrderAppProductVariantBusinessDetailsQuery,
  OrderAppProductVariantBusinessDetailsQueryVariables,
} from '@/graphql/orderApp/api';
import { ProductVariantBusinessDetailsDocument } from '@/graphql/orderApp/api';
import type {
  Image,
  ProductsByMarketingPartnerQuery,
  ProductsByMarketingPartnerQueryVariables,
  ProductVariantPriceQuery,
  ProductVariantPriceQueryVariables,
  ProductVariantWatchBasicDetailsFragment,
  ProductVariantWatchBasicDetailsQuery,
  ProductVariantWatchBasicDetailsQueryVariables,
  VariantPricingFragment,
} from '@/graphql/saleor/api';
import {
  ProductsByMarketingPartnerDocument,
  ProductVariantPriceDocument,
  ProductVariantWatchBasicDetailsDocument,
} from '@/graphql/saleor/api';
import {
  checkProductActivation,
  getDesignerChoiceByPartialSku,
  getProduct,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { WatchHit } from '@/integrations/algolia/types';
import {
  fetchDetailPageDataWithExtraData,
  fetchSelectorModalByMarketingPartner,
} from '@/integrations/contentful';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import {
  DETAIL_PAGE_IMAGE_SIZE,
  DETAIL_PAGE_LARGE_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  WORLD_WIDE_CATALOG_COUNTRY,
} from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import { isSupportedLocale } from '@/lib/locales/locales';
import { serializeWatchPdpMarkdown, withMarkdownExport } from '@/lib/markdown';
import { WATCH_PDP_ERRORS } from '@/lib/notFoundErrors';
import type { UrlOpts } from '@/lib/paths';
import { paths } from '@/lib/paths';
import {
  makeWatchCanonicalFromVariant,
  makeWatchProductUrl,
  updateProductVariantPricing,
} from '@/lib/product';
import {
  getContentfulRegionFromLocale,
  getGqlRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import { SEO_HREFLANG_COUNTRIES } from '@/lib/seo/Config';
import { makeActivatedProductHrefLangs } from '@/lib/seo/Functions';
import {
  extractProductStructuredData,
  extractSeoVariablesFromWatchProductVariant,
} from '@/lib/seo/SeoVariables';
import { createServerMemo } from '@/lib/serverMemo';
import { timed } from '@/lib/serverTiming';
import { isWatchSku } from '@/lib/util';
import type { WatchViewProps } from '@/views/WatchView';
import { areParamsValid } from '@/views/WatchView/ssr';
import { WatchViewWrapper } from '@/views/WatchView/WatchViewWrapper';

const selectorModalMemo = createServerMemo<
  Awaited<ReturnType<typeof fetchSelectorModalByMarketingPartner>>
>({ max: 500, ttlMs: 10 * 60 * 1000 });

type WatchPdpParams = {
  collection: string;
  lang: string;
  model: string;
  sku: string;
};

type WatchPdpContext = GetServerSidePropsContext<WatchPdpParams>;

type WatchProductParams = Pick<WatchPdpParams, 'collection' | 'model' | 'sku'>;

const appendQueryString = (url: string, queryString: string | null) =>
  queryString ? `${url}?${queryString}` : url;

const generateDestination = (
  locale: string | undefined,
  path: string | UrlOpts,
  queryString: string | null,
) => {
  const urlPath = addTrailingSlash(path as string);
  return appendQueryString(`/${locale}${urlPath}`, queryString);
};

const generateFullResolvedUrl = (
  locale: string | undefined,
  resolvedUrl: string,
  queryString: string | null,
  host: string | undefined,
) => {
  const resolvedUrlWithoutLocale = removeLocaleFromUrl(resolvedUrl);
  const url = new URL(resolvedUrlWithoutLocale, `https://${host}`);
  return appendQueryString(`/${locale}${url.pathname}`, queryString);
};

const getMovedDestination = (
  context: WatchPdpContext,
  locale: string | undefined,
  path: string | UrlOpts,
  queryString: string,
) => {
  const destination = generateDestination(locale, path, queryString);
  const fullResolvedUrl = generateFullResolvedUrl(
    locale,
    context.resolvedUrl,
    queryString,
    context.req?.headers?.host,
  );
  return fullResolvedUrl === destination ? undefined : destination;
};

const getLegacyWatchRedirect = async (
  context: WatchPdpContext,
  { collection, model, sku }: WatchProductParams,
  routerLocale: string | undefined,
  queryString: string,
  draftMode: boolean,
) => {
  const watchQuery = context.query?.watch;
  if (watchQuery && typeof watchQuery === 'string' && isWatchSku(watchQuery)) {
    const destination = getMovedDestination(
      context,
      routerLocale,
      paths.watches.collection.model.product.asPath({
        model,
        collection,
        sku: watchQuery,
      }),
      queryString,
    );
    if (destination) {
      return { redirect: { destination, permanent: false } };
    }
  }

  if (sku.length !== 11) {
    return undefined;
  }

  const designerChoice = await timed(
    context.res,
    'algolia_designer_choice',
    getDesignerChoiceByPartialSku(sku, routerLocale ?? '', draftMode),
  );
  if (!designerChoice) {
    return undefined;
  }

  const destination = getMovedDestination(
    context,
    routerLocale,
    paths.watches.collection.model.product.asPath({
      model,
      collection,
      sku: designerChoice.objectID,
    }),
    queryString,
  );
  return destination
    ? { redirect: { destination, permanent: true } }
    : undefined;
};

const watchNotFound = (
  context: WatchPdpContext,
  { collection, model, sku }: WatchProductParams,
  reason: { code: string; message: string },
) => {
  capture404Event('Watch PDP', {
    requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
    collection,
    model,
    sku,
    reason,
  });

  return { notFound: true as const };
};

const fetchSelectorModal = (
  marketingPartnerName: string,
  draftMode: boolean,
  contentfulLocale: string,
) =>
  draftMode
    ? fetchSelectorModalByMarketingPartner(
        marketingPartnerName,
        draftMode,
        contentfulLocale,
      )
    : selectorModalMemo(`${marketingPartnerName}|${contentfulLocale}`, () =>
        fetchSelectorModalByMarketingPartner(
          marketingPartnerName,
          draftMode,
          contentfulLocale,
        ),
      );

// TODO:
// This needs to be figured out. Currently we do not know how to distinguish collection types
// like find-your-watch

export const getServerSideProps = withMarkdownExport(
  serializeWatchPdpMarkdown,
  commonServerSideProps<WatchViewProps, WatchPdpParams>(async (context) => {
    const { params, query } = context;
    const draftMode = resolveContentDraftMode(context);
    const routerLocale = getLocaleFromParams(context?.params);

    if (!isSupportedLocale(routerLocale)) {
      return { notFound: true };
    }

    const { collection, model, sku } = params!;
    const queryString = new URLSearchParams(
      query as Record<string, string>,
    ).toString();

    const legacyRedirect = await getLegacyWatchRedirect(
      context,
      { collection, model, sku },
      routerLocale,
      queryString,
      draftMode,
    );
    if (legacyRedirect) {
      return legacyRedirect;
    }

    const region = getRegionFromLocale(routerLocale);
    const regionVariables = getGqlRegionFromLocale(routerLocale);
    const client = initializeApollo(undefined, true);
    const securedClient = initializeSecureApolloClient();
    const { countryCode } = getContentfulRegionFromLocale(routerLocale);
    const contentfulLocale = region.locale.contentful ?? region.locale.locale;
    const algoliaSsrSearchClient = createSearchClient({
      isPreview: draftMode,
      isSsr: true,
    });

    const [
      { data: productVariantData },
      { cmsData, cmsDefaultLayoutData, cmsExtraData, seoConfigBuilder },
      businessData,
      { data: productPriceData },
      watchBySkuParam,
    ] = await Promise.all([
      timed(
        context.res,
        'saleor_variant',
        securedClient.query<
          ProductVariantWatchBasicDetailsQuery,
          ProductVariantWatchBasicDetailsQueryVariables
        >({
          query: ProductVariantWatchBasicDetailsDocument,
          variables: {
            sku: sku.toUpperCase(),
            languageCode: regionVariables.languageCode,
            thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
            imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
            largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
          },
        }),
      ),
      timed(
        context.res,
        'cms_detail_page',
        fetchDetailPageDataWithExtraData(
          'watch',
          sku,
          contentfulLocale,
          collection,
          model,
          regionVariables.languageCode,
          regionVariables.channel,
          countryCode,
          draftMode,
        ),
      ),
      timed(
        context.res,
        'orderapp_business',
        client.query<
          OrderAppProductVariantBusinessDetailsQuery,
          OrderAppProductVariantBusinessDetailsQueryVariables
        >({
          query: ProductVariantBusinessDetailsDocument,
          variables: {
            sku,
            channel: regionVariables.channel,
          },
          context: CustomContext({
            client: APOLLO_CLIENTS.orderApp,
            disableErrorNotification: true,
          }),
        }),
      ),
      timed(
        context.res,
        'saleor_price',
        client.query<
          ProductVariantPriceQuery,
          ProductVariantPriceQueryVariables
        >({
          query: ProductVariantPriceDocument,
          variables: {
            sku: sku,
            channel: regionVariables.channel,
          },
        }),
      ),
      timed(
        context.res,
        'algolia_product',
        getProduct<WatchHit>(
          routerLocale,
          sku.toUpperCase(),
          algoliaSsrSearchClient,
        ),
      ),
    ]);
    const missingProductVariant = !productVariantData.productVariant;
    const missingCmsData = !cmsData || cmsData.length === 0;

    // The query returns the gross price. In the view we only use the gross price but we nee to build the full object.
    // This is needed when the previous getVariant doesn't return the price.
    const data = updateProductVariantPricing(
      productVariantData,
      productPriceData,
    );

    if (
      missingProductVariant ||
      missingCmsData ||
      !data.productVariant?.product ||
      !data.productVariant?.sku
    ) {
      return watchNotFound(
        context,
        { collection, model, sku },
        missingProductVariant
          ? WATCH_PDP_ERRORS.VARIANT_MISSING
          : WATCH_PDP_ERRORS.CMS_DATA_MISSING,
      );
    }

    // Gate on the catalog before building URLs/SEO so non-browsable SKUs 404 instead of throwing.
    const variantSku = data.productVariant.sku;
    const watch =
      variantSku === sku.toUpperCase()
        ? watchBySkuParam
        : await timed(
            context.res,
            'algolia_product_by_variant_sku',
            getProduct<WatchHit>(
              routerLocale,
              variantSku,
              algoliaSsrSearchClient,
            ),
          );

    const watchActivation = checkProductActivation(watch, draftMode);
    const { isPdpActivated } = watchActivation;
    const isDesignerChoice = watch?.designer_choice === true;
    if (!watch || !isPdpActivated) {
      return watchNotFound(
        context,
        { collection, model, sku },
        WATCH_PDP_ERRORS.PRODUCT_NOT_BROWSABLE,
      );
    }

    const productCollections = data.productVariant.product.collections ?? [];
    const productModelSlug = data.productVariant.product.model?.values[0]?.slug;
    if (productCollections.length === 0 || !productModelSlug) {
      return watchNotFound(
        context,
        { collection, model, sku },
        WATCH_PDP_ERRORS.URL_ATTRIBUTES_MISSING,
      );
    }

    const path = makeWatchProductUrl(
      collection,
      productCollections,
      productModelSlug,
      variantSku,
      true,
    );

    // Malformed params, redirect to proper path.
    const paramsRedirectDestination =
      !areParamsValid(collection, model, data) &&
      getMovedDestination(context, routerLocale, path, queryString);
    if (paramsRedirectDestination) {
      return {
        redirect: {
          destination: paramsRedirectDestination,
          permanent: true,
        },
      };
    }

    const marketingPartnerName =
      (data?.productVariant?.product?.marketingPartner?.values[0]
        ?.name as string) ?? '';

    const marketingPartnerSlug =
      (data?.productVariant?.product?.marketingPartner?.values[0]
        ?.slug as string) ?? '';

    const strapMaterialSlug =
      (data?.productVariant?.product?.strapMaterial?.values[0]
        ?.slug as string) ?? '';

    const [
      marketingPartnerProductsResult,
      marketingSelectorModalResult,
      canonicalUrl,
      activatedHreflangs,
    ] = await Promise.all([
      timed(
        context.res,
        'saleor_marketing_partner',
        securedClient.query<
          ProductsByMarketingPartnerQuery,
          ProductsByMarketingPartnerQueryVariables
        >({
          query: ProductsByMarketingPartnerDocument,
          variables: {
            modelName: model as string,
            strapMaterial: strapMaterialSlug,
            marketingPartner: marketingPartnerSlug,
          },
        }),
      ),
      timed(
        context.res,
        'cms_selector_modal',
        fetchSelectorModal(marketingPartnerName, draftMode, contentfulLocale),
      ),
      isDesignerChoice
        ? 'watch'
        : timed(
            context.res,
            'algolia_canonical',
            makeWatchCanonicalFromVariant(
              data,
              model,
              collection,
              routerLocale,
              algoliaSsrSearchClient,
            ),
          ),
      timed(
        context.res,
        'algolia_hreflangs',
        makeActivatedProductHrefLangs(
          variantSku,
          routerLocale ?? '',
          SEO_HREFLANG_COUNTRIES,
          [],
          draftMode,
        ),
      ),
    ]);

    const seoConfig = seoConfigBuilder
      ?.mergeSeoVariables({
        ...extractSeoVariablesFromWatchProductVariant(
          data.productVariant.product,
          region.locale.slug,
        ),
        thumbnail: data?.productVariant?.product?.thumbnail as Image,
        productName:
          (data?.productVariant?.product?.watchName?.values[0]?.translation
            ?.plainText as string | undefined) ??
          data?.productVariant?.product.name,
      })
      .setCanonical(canonicalUrl)
      .setHreflangs(activatedHreflangs)
      .build();

    const productCatalogMode = WORLD_WIDE_CATALOG_COUNTRY?.includes(
      region.country.code,
    );
    const productStructuredData =
      extractProductStructuredData<ProductVariantWatchBasicDetailsFragment>(
        data.productVariant as ProductVariantWatchBasicDetailsFragment & {
          pricing: VariantPricingFragment;
        },
        process.env.NEXT_PUBLIC_URL + `${routerLocale}${path}`,
        productCatalogMode,
        businessData.data.productVariant?.variantCTA,
      );

    setCacheHeader(CacheHeaderType.NO_CACHE_TAG, context);

    return {
      props: {
        productVariant: data.productVariant,
        cmsData,
        cmsDefaultLayoutData,
        cmsExtraDataRecord: cmsExtraData,
        draftMode: draftMode ?? false,
        seoConfig,
        productStructuredData,
        businessData: businessData.data,
        marketingPartnerProductsResult: marketingPartnerProductsResult,
        marketingSelectorModalResult: marketingSelectorModalResult,
      },
    };
  }),
);

export default WatchViewWrapper;
